import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty, Banner, fmtDate } from '../../components/ui';
import { Panel } from '../../components/charts';
import {
  Blockers,
  MAR_STATE,
  PO_CHIP,
  PO_STATE,
  qtyOf,
  slipMeasured,
  type ProcRow,
} from './Dashboard';
import type { ProcOrder } from './Orders';
import type { ProcDelivery } from './Deliveries';

/* ============================================================
   Paket procurement — daftar dan detail.

   Detail memuat gerbang MAR, dan gerbang itu ditampilkan TERBUKA:
   tombol terbitkan PO tetap terlihat walau mati, dengan alasannya
   tertulis di sebelahnya. Tombol yang disembunyikan mengajarkan
   orang bahwa fiturnya tidak ada; tombol mati yang menyebut
   sebabnya mengajarkan mereka dokumen mana yang harus dikejar.
   ============================================================ */

export type ProcStepRow = {
  step: string;
  label: string;
  plannedStart: string | null;
  plannedFinish: string | null;
  forecastStart: string | null;
  forecastFinish: string | null;
  actualStart: string | null;
  actualFinish: string | null;
  shiftDays: number;
  state: 'DONE' | 'CURRENT' | 'PENDING';
};

type PackageRow = ProcRow & { steps: ProcStepRow[] };

type Detail = {
  package: ProcRow & {
    projectId: string;
    currentStep: string | null;
    mar: {
      docId: string;
      reference: string;
      title: string;
      state: string;
      plannedApprovalOn: string | null;
      actualApprovalOn: string | null;
    } | null;
  };
  steps: ProcStepRow[];
  orders: ProcOrder[];
  deliveries: ProcDelivery[];
};

const STEP_STATE: Record<string, string> = {
  DONE: 'Terlewati',
  CURRENT: 'Sedang di sini',
  PENDING: 'Belum',
};

const STEP_CHIP: Record<string, string> = { DONE: 'good', CURRENT: 'blue', PENDING: '' };

/**
 * Posisi paket di tangga tujuh langkah, tanpa teks.
 *
 * Warna tidak pernah jadi satu-satunya pembawa arti di sini: nama
 * langkah yang sedang berjalan selalu ditulis di bawahnya, dan tiap
 * titik membawa judulnya sendiri.
 */
function StepDots({ steps }: { steps: ProcStepRow[] }) {
  const at = steps.find((s) => s.state === 'CURRENT');
  return (
    <div
      className="rail"
      style={{ margin: 0 }}
      role="img"
      aria-label={`Langkah ${at ? at.label : 'belum berjalan'} dari tujuh`}
    >
      {steps.map((s, i) => (
        <span key={s.step} style={{ display: 'contents' }}>
          {i > 0 && <span className={`rail-line ${s.state === 'DONE' ? 'done' : ''}`} />}
          <span className="rail-step">
            <span
              className={`rail-dot ${s.state === 'DONE' ? 'done' : s.state === 'CURRENT' ? 'now' : ''}`}
              title={`${s.label} — ${STEP_STATE[s.state] ?? s.state}`}
            />
          </span>
        </span>
      ))}
    </div>
  );
}

export function Packages({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [packages, setPackages] = useState<PackageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ packages: PackageRow[] }>(`/proc/packages?projectId=${projectId}`);
      setPackages(r.packages);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load procurement packages.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!packages) return <Loading rows={5} />;

  const q = search.trim().toLowerCase();
  const shown = packages.filter((p) => {
    if (state && p.poState !== state) return false;
    if (!q) return true;
    return [p.code, p.name, ...p.boqCodes].some((v) => v.toLowerCase().includes(q));
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Paket</h1>
          <p>
            Diurutkan dari yang paling terlambat, lalu dari yang paling banyak penghalangnya. Klik
            satu baris untuk melihat tujuh langkahnya.
          </p>
        </div>
      </div>

      <div className="filters">
        <input
          className="input"
          placeholder="Cari kode paket, nama, atau kode BOQ"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="select" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">Semua status PO</option>
          {Object.entries(PO_STATE).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      {packages.length === 0 ? (
        <Empty
          title="Belum ada paket procurement"
          body="Paket dibuat dari kode BOQ yang sudah dimekarkan, lalu ditautkan ke MAR-nya dan ke tujuh langkah di programme."
        />
      ) : shown.length === 0 ? (
        <Empty title="Tidak ada paket yang cocok" body="Longgarkan penyaringnya." />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Paket</th>
                <th>Nama</th>
                <th>Langkah</th>
                <th>PO</th>
                <th>Terkirim</th>
                <th>Slip</th>
                <th>Tertahan karena</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => {
                const at = p.steps.find((s) => s.state === 'CURRENT');
                const done = p.steps.filter((s) => s.state === 'DONE').length;
                return (
                  <tr key={p.id} onClick={() => nav(`/packages/${p.id}`)}>
                    <td className="num">{p.code}</td>
                    <td>{p.name}</td>
                    <td>
                      {p.steps.length === 0 ? (
                        <span className="hint">tidak ada langkah di programme</span>
                      ) : (
                        <>
                          <StepDots steps={p.steps} />
                          <div className="hint">
                            {at ? at.label : 'tidak sedang berjalan'} · {done} dari {p.steps.length}{' '}
                            terlewati
                          </div>
                        </>
                      )}
                    </td>
                    <td>
                      <span className={`chip ${PO_CHIP[p.poState] ?? ''}`}>
                        {PO_STATE[p.poState] ?? p.poState}
                      </span>
                      {p.poNo && <div className="hint">{p.poNo}</div>}
                    </td>
                    <td>
                      {qtyOf(p.deliveredQty, p.unit)}
                      <div className="hint">
                        dari {p.orderedQty > 0 ? qtyOf(p.orderedQty, p.unit) : 'kuantitas yang belum ditetapkan'}
                      </div>
                    </td>
                    <td className={slipMeasured(p) && p.slipDays > 0 ? 'neg' : ''}>
                      {slipMeasured(p) ? `${p.slipDays} hari` : '—'}
                    </td>
                    <td>
                      <Blockers items={p.blockers} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ============================================================ */

export function PackageDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [poError, setPoError] = useState<string | null>(null);
  const [poBusy, setPoBusy] = useState(false);
  const [override, setOverride] = useState(false);
  const [po, setPo] = useState({
    poNo: '',
    supplierName: '',
    qty: '',
    unit: '',
    issuedOn: '',
    reason: '',
  });

  const [dnError, setDnError] = useState<string | null>(null);
  const [dnBusy, setDnBusy] = useState(false);
  const [dn, setDn] = useState({ poId: '', noteNo: '', deliveredOn: '', qty: '', note: '' });

  const load = useCallback(async () => {
    try {
      const d = await api.get<Detail>(`/proc/packages/${id}`);
      setData(d);
      // Prasetel dari paket, bukan angka karangan: kuantitas rencana
      // dan satuan paket adalah tebakan terbaik yang punya sumber.
      setPo((f) => ({
        ...f,
        qty: f.qty || (d.package.orderedQty > 0 ? String(d.package.orderedQty) : ''),
        unit: f.unit || d.package.unit,
      }));
      setDn((f) => ({
        ...f,
        poId: f.poId || (d.orders.find((o) => o.state === 'ISSUED')?.id ?? ''),
      }));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the package.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function issue(e: React.FormEvent) {
    e.preventDefault();
    setPoBusy(true);
    setPoError(null);
    try {
      const r = await api.post<{ poNo: string; overridden: boolean }>(`/proc/packages/${id}/po`, {
        poNo: po.poNo.trim(),
        supplierName: po.supplierName.trim() || null,
        qty: Number(po.qty),
        unit: po.unit.trim() || null,
        issuedOn: po.issuedOn,
        override: override ? { reason: po.reason.trim() } : null,
      });
      setPo((f) => ({ ...f, poNo: '', supplierName: '', reason: '' }));
      setOverride(false);
      setNotice(
        r.overridden
          ? `PO ${r.poNo} terbit dengan terobosan. Nama dan alasannya tercatat di audit.`
          : `PO ${r.poNo} terbit.`,
      );
      await load();
    } catch (err) {
      // Apa adanya. Kalau yang menolak adalah gerbang MAR, kalimat
      // itulah yang harus dibaca orang, bukan ringkasan buatan
      // halaman ini.
      setPoError(err instanceof ApiError ? err.message : 'Gagal menerbitkan PO.');
    } finally {
      setPoBusy(false);
    }
  }

  async function deliver(e: React.FormEvent) {
    e.preventDefault();
    if (!dn.poId) return;
    setDnBusy(true);
    setDnError(null);
    try {
      const r = await api.post<{ noteNo: string; shortfallQty: number; complete: boolean }>(
        `/proc/orders/${dn.poId}/deliveries`,
        {
          noteNo: dn.noteNo.trim(),
          deliveredOn: dn.deliveredOn,
          qty: Number(dn.qty),
          note: dn.note.trim() || null,
        },
      );
      setDn((f) => ({ ...f, noteNo: '', qty: '', note: '' }));
      setNotice(
        r.complete
          ? `Surat jalan ${r.noteNo} tercatat. Pesanan ini lengkap.`
          : `Surat jalan ${r.noteNo} tercatat. Masih kurang ${r.shortfallQty}.`,
      );
      await load();
    } catch (err) {
      setDnError(err instanceof ApiError ? err.message : 'Gagal mencatat surat jalan.');
    } finally {
      setDnBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const p = data.package;
  const mar = p.mar;
  const blocked = p.poState === 'BLOCKED';
  const issuedOrders = data.orders.filter((o) => o.state === 'ISSUED');

  /* Alasan gerbang, dalam kalimat yang menyebut dokumen yang harus
     dikejar. "Blocked" tidak memberi tahu siapa pun harus menelepon
     siapa. */
  const gateReason = !blocked
    ? null
    : mar == null
      ? 'Paket ini belum ditautkan ke MAR mana pun, jadi tidak ada yang bisa disetujui dan PO tidak boleh terbit.'
      : `MAR ${mar.reference} berstatus "${MAR_STATE[mar.state] ?? mar.state}" — belum disetujui, jadi PO tidak boleh terbit.`;

  const canIssue = !blocked || (override && po.reason.trim() !== '');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{p.code}</h1>
          <p>{p.name}</p>
        </div>
        <div className="filters" style={{ marginBottom: 0 }}>
          <span className={`chip ${PO_CHIP[p.poState] ?? ''}`}>
            {PO_STATE[p.poState] ?? p.poState}
          </span>
          {p.currentStep && (
            <span className="chip blue">
              {data.steps.find((s) => s.step === p.currentStep)?.label ?? p.currentStep}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => nav('/packages')}>
            Kembali
          </button>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      {p.blockers.length > 0 && (
        <div className="banner">
          {p.blockers.map((b) => (
            <div key={b}>{b}</div>
          ))}
        </div>
      )}

      <div className="grid-2">
        <Panel title="Paket" note="Kekurangan kirim diukur terhadap kuantitas PO begitu PO ada.">
          <div className="tbl-wrap">
            <table className="tbl">
              <tbody>
                <tr className="no-click">
                  <td>Kode BOQ</td>
                  <td>{p.boqCodes.length > 0 ? p.boqCodes.join(', ') : '—'}</td>
                </tr>
                <tr className="no-click">
                  <td>Dipesan</td>
                  <td>{p.orderedQty > 0 ? qtyOf(p.orderedQty, p.unit) : '— belum ditetapkan'}</td>
                </tr>
                <tr className="no-click">
                  <td>Terkirim</td>
                  <td>
                    {qtyOf(p.deliveredQty, p.unit)}
                    {p.orderedQty > 0 && ` · ${p.pct}%`}
                  </td>
                </tr>
                <tr className="no-click">
                  <td>Kurang</td>
                  <td className={p.orderedQty > 0 && p.shortfallQty > 0 ? 'neg' : ''}>
                    {p.orderedQty > 0 ? qtyOf(p.shortfallQty, p.unit) : '—'}
                  </td>
                </tr>
                <tr className="no-click">
                  <td>Surat jalan</td>
                  <td>{p.noteCount}</td>
                </tr>
                <tr className="no-click">
                  <td>Tiba di site — rencana</td>
                  <td>{p.onSitePlanned ? fmtDate(p.onSitePlanned) : '— tidak ada di programme'}</td>
                </tr>
                <tr className="no-click">
                  <td>Tiba di site — perkiraan</td>
                  <td>{p.onSiteForecast ? fmtDate(p.onSiteForecast) : '— belum terhitung'}</td>
                </tr>
                <tr className="no-click">
                  <td>Slip</td>
                  <td className={slipMeasured(p) && p.slipDays > 0 ? 'neg' : ''}>
                    {slipMeasured(p) ? `${p.slipDays} hari kerja` : '— tidak terhitung'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel
          title="MAR"
          note="Persetujuan MAR adalah sebab pertama di rantai. Tanggal rencananya datang dari SLA dokumen, bukan dari tenggat yang bergeser tiap putaran revisi."
        >
          {mar == null ? (
            <p className="hint">
              Paket ini belum ditautkan ke MAR mana pun. Selama begitu, pergeseran PO tidak punya
              titik ukur dan PO tidak boleh terbit tanpa terobosan.
            </p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <tbody>
                  <tr className="no-click">
                    <td>Dokumen</td>
                    <td className="num">{mar.reference}</td>
                  </tr>
                  <tr className="no-click">
                    <td>Judul</td>
                    <td>{mar.title}</td>
                  </tr>
                  <tr className="no-click">
                    <td>Status</td>
                    <td>{MAR_STATE[mar.state] ?? mar.state}</td>
                  </tr>
                  <tr className="no-click">
                    <td>Rencana disetujui</td>
                    <td>
                      {mar.plannedApprovalOn ? fmtDate(mar.plannedApprovalOn) : '— belum terhitung'}
                    </td>
                  </tr>
                  <tr className="no-click">
                    <td>Benar-benar disetujui</td>
                    <td>{mar.actualApprovalOn ? fmtDate(mar.actualApprovalOn) : '— belum'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <Panel
        title="Tujuh langkah"
        note="Rencana datang dari programme; perkiraan adalah rencana yang sudah digeser oleh keterlambatan MAR."
        wide
      >
        {data.steps.length === 0 ? (
          <p className="hint">
            Paket ini tidak punya satu pun langkah di programme, jadi tidak ada rencana yang bisa
            digeser dan tidak ada tanggal tiba yang bisa dijanjikan.
          </p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Langkah</th>
                  <th>Status</th>
                  <th>Rencana mulai</th>
                  <th>Rencana selesai</th>
                  <th>Perkiraan mulai</th>
                  <th>Perkiraan selesai</th>
                  <th>Aktual selesai</th>
                  <th>Geser</th>
                </tr>
              </thead>
              <tbody>
                {data.steps.map((s) => (
                  <tr className="no-click" key={s.step}>
                    <td className="num">{s.label}</td>
                    <td>
                      <span className={`chip ${STEP_CHIP[s.state] ?? ''}`}>
                        {STEP_STATE[s.state] ?? s.state}
                      </span>
                    </td>
                    <td>{s.plannedStart ? fmtDate(s.plannedStart) : '—'}</td>
                    <td>{s.plannedFinish ? fmtDate(s.plannedFinish) : '—'}</td>
                    <td>{s.forecastStart ? fmtDate(s.forecastStart) : '—'}</td>
                    <td>{s.forecastFinish ? fmtDate(s.forecastFinish) : '—'}</td>
                    <td>{s.actualFinish ? fmtDate(s.actualFinish) : '—'}</td>
                    {/* shiftDays tidak boleh dibaca sendirian: kolomnya
                        tidak bisa null, jadi perkiraan yang kosong itulah
                        yang menandai "belum terukur". */}
                    <td className={s.forecastFinish != null && s.shiftDays > 0 ? 'neg' : ''}>
                      {s.forecastFinish == null ? '—' : `${s.shiftDays} hari`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title="Terbitkan PO"
        note="Membeli barang sebelum spesifikasinya disetujui adalah cara paling mahal untuk salah, jadi gerbangnya menahan dan tidak sekadar memperingatkan."
        wide
      >
        {poError && <Banner message={poError} />}

        <form onSubmit={issue}>
          <div className="filters">
            <input
              className="input"
              placeholder="Nomor PO"
              value={po.poNo}
              onChange={(e) => setPo({ ...po, poNo: e.target.value })}
              required
            />
            <input
              className="input"
              placeholder="Pemasok"
              value={po.supplierName}
              onChange={(e) => setPo({ ...po, supplierName: e.target.value })}
            />
            <input
              className="input"
              type="number"
              step="any"
              placeholder="Kuantitas"
              value={po.qty}
              onChange={(e) => setPo({ ...po, qty: e.target.value })}
              required
            />
            <input
              className="input"
              placeholder="Satuan"
              title="Satuan"
              value={po.unit}
              onChange={(e) => setPo({ ...po, unit: e.target.value })}
            />
            <input
              className="input"
              type="date"
              title="Tanggal terbit"
              value={po.issuedOn}
              onChange={(e) => setPo({ ...po, issuedOn: e.target.value })}
              required
            />
            <button className="btn btn-primary" disabled={poBusy || !canIssue}>
              Terbitkan
            </button>
            {gateReason && <span className="hint">{gateReason}</span>}
          </div>

          {blocked && (
            <>
              <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={override}
                  onChange={(e) => setOverride(e.target.checked)}
                />
                Terbitkan dengan terobosan — nama Anda dan alasannya masuk ke PO dan ke catatan audit.
              </label>
              {override && (
                <input
                  className="input"
                  style={{ marginTop: 10 }}
                  placeholder="Alasan terobosan — wajib"
                  value={po.reason}
                  onChange={(e) => setPo({ ...po, reason: e.target.value })}
                />
              )}
            </>
          )}
        </form>
      </Panel>

      <Panel title="Purchase order" note="Yang terbit paling awal memerintah tanggal pabrikasi paket ini." wide>
        {data.orders.length === 0 ? (
          <p className="hint">Belum ada PO pada paket ini.</p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>PO</th>
                  <th>Pemasok</th>
                  <th>Terbit</th>
                  <th>Status</th>
                  <th>Dipesan</th>
                  <th>Terkirim</th>
                  <th>Kurang</th>
                  <th>Terobosan</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((o) => (
                  <tr className="no-click" key={o.id}>
                    <td className="num">{o.poNo}</td>
                    <td>{o.supplierName ?? '—'}</td>
                    <td>{o.issuedOn ? fmtDate(o.issuedOn) : '—'}</td>
                    <td>
                      <span className={`chip ${PO_CHIP[o.state] ?? ''}`}>
                        {PO_STATE[o.state] ?? o.state}
                      </span>
                    </td>
                    <td>{o.qty > 0 ? qtyOf(o.qty, o.unit) : '—'}</td>
                    <td>{qtyOf(o.deliveredQty, o.unit)}</td>
                    <td className={o.qty > 0 && o.shortfallQty > 0 ? 'neg' : ''}>
                      {o.qty > 0 ? qtyOf(o.shortfallQty, o.unit) : '—'}
                    </td>
                    <td>{o.overridden ? (o.overrideReason ?? 'tanpa alasan tercatat') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title="Surat jalan"
        note="Kuantitas negatif diterima: retur itu nyata, dan barangnya benar-benar pergi dari site."
        wide
      >
        {dnError && <Banner message={dnError} />}

        {issuedOrders.length === 0 ? (
          <p className="hint">
            Belum ada PO yang terbit pada paket ini, jadi belum ada apa pun yang bisa dikirim
            terhadapnya.
          </p>
        ) : (
          <form onSubmit={deliver}>
            <div className="filters">
              <select
                className="select"
                value={dn.poId}
                onChange={(e) => setDn({ ...dn, poId: e.target.value })}
                required
              >
                {issuedOrders.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.poNo} — kurang {o.qty > 0 ? qtyOf(o.shortfallQty, o.unit) : 'belum terukur'}
                  </option>
                ))}
              </select>
              <input
                className="input"
                placeholder="Nomor surat jalan"
                value={dn.noteNo}
                onChange={(e) => setDn({ ...dn, noteNo: e.target.value })}
                required
              />
              <input
                className="input"
                type="date"
                title="Tanggal kirim"
                value={dn.deliveredOn}
                onChange={(e) => setDn({ ...dn, deliveredOn: e.target.value })}
                required
              />
              <input
                className="input"
                type="number"
                step="any"
                placeholder="Kuantitas"
                value={dn.qty}
                onChange={(e) => setDn({ ...dn, qty: e.target.value })}
                required
              />
              <input
                className="input"
                placeholder="Catatan"
                value={dn.note}
                onChange={(e) => setDn({ ...dn, note: e.target.value })}
              />
              <button className="btn btn-primary" disabled={dnBusy}>
                Catat
              </button>
            </div>
          </form>
        )}

        {data.deliveries.length === 0 ? (
          <p className="hint">Belum ada surat jalan pada paket ini.</p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Surat jalan</th>
                  <th>Tanggal</th>
                  <th>PO</th>
                  <th>Kuantitas</th>
                  <th>Diterima oleh</th>
                  <th>Catatan</th>
                </tr>
              </thead>
              <tbody>
                {data.deliveries.map((d) => (
                  <tr className="no-click" key={d.id}>
                    <td className="num">{d.noteNo}</td>
                    <td>{fmtDate(d.deliveredOn)}</td>
                    <td>{d.poNo}</td>
                    <td className={d.qty < 0 ? 'neg' : ''}>{qtyOf(d.qty, d.unit)}</td>
                    <td>{d.receivedBy ?? '—'}</td>
                    <td>{d.note ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
